import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { clientAPI, authAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import KenyaLocationFields from '../../components/common/KenyaLocationFields';
import toast from 'react-hot-toast';
import {
  UserIcon,
  PhoneIcon,
  MapPinIcon,
  BellIcon,
  EnvelopeIcon,
  ArrowUpTrayIcon,
  CameraIcon
} from '@heroicons/react/24/outline';

const ClientSettings = () => {
  const { user, updateLocalUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    county: '',
    subCounty: '',
    town: '',
    avatarUrl: '',
    defaultAddress: '',
    // Order notification preferences (clients only get order notifications)
    notifyOnOrderConfirmed: true,
    notifyOnOrderStatusChange: true,
    notifyOnOrderDelivered: true
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      // Load user data
      setFormData({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        phone: user?.phone || '',
        county: user?.county || '',
        subCounty: user?.subCounty || '',
        town: user?.town || '',
        avatarUrl: user?.avatarUrl || '',
        defaultAddress: '',
        notifyOnOrderConfirmed: true,
        notifyOnOrderStatusChange: true,
        notifyOnOrderDelivered: true
      });
      setAvatarPreview(user?.avatarUrl || null);
      
      // Try to load client profile for additional settings
      try {
        const res = await clientAPI.getProfile();
        const clientProfile = res.data.profile;
        if (clientProfile) {
          setProfile(clientProfile);
          setFormData(prev => ({
            ...prev,
            county: clientProfile.user?.county || prev.county,
            subCounty: clientProfile.user?.subCounty || prev.subCounty,
            town: clientProfile.user?.town || prev.town,
            defaultAddress: clientProfile.defaultAddress || '',
            notifyOnOrderConfirmed: clientProfile.metadata?.notifyOnOrderConfirmed ?? true,
            notifyOnOrderStatusChange: clientProfile.metadata?.notifyOnOrderStatusChange ?? true,
            notifyOnOrderDelivered: clientProfile.metadata?.notifyOnOrderDelivered ?? true
          }));
        }
      } catch (e) {
        // Client profile might not exist yet
        console.log('No client profile found');
      }
    } catch (error) {
      console.error('Load profile error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setAvatarPreview(previewUrl);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Upload avatar if a new file was selected
      let avatarUrl = formData.avatarUrl;
      if (avatarFile) {
        try {
          const avatarRes = await authAPI.uploadAvatar(avatarFile);
          avatarUrl = avatarRes?.data?.url || formData.avatarUrl;
        } catch (avatarError) {
          console.error('Avatar upload error:', avatarError);
          toast.error('Failed to upload profile picture');
        }
      }
      
      const profileRes = await authAPI.updateProfile({
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        county: formData.county,
        subCounty: formData.subCounty,
        town: formData.town,
        avatarUrl
      });
      if (profileRes.data?.user) updateLocalUser(profileRes.data.user);
      
      // Update client profile (delivery address and notification preferences)
      await clientAPI.updateProfile({
        defaultAddress: formData.defaultAddress,
        metadata: {
          notifyOnOrderConfirmed: formData.notifyOnOrderConfirmed,
          notifyOnOrderStatusChange: formData.notifyOnOrderStatusChange,
          notifyOnOrderDelivered: formData.notifyOnOrderDelivered
        }
      });
      
      // Clear the file input and update local state
      setAvatarFile(null);
      
      toast.success('Settings updated successfully');
      loadProfile();
    } catch (error) {
      toast.error(error.response?.data?.details || error.response?.data?.message || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Account Settings</h1>
        <p className="text-gray-600">Manage your profile and notification preferences</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Personal Information */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
            <UserIcon className="h-5 w-5 mr-2 text-gray-500" />
            Personal Information
          </h3>
          
          {/* Profile Picture */}
          <div className="mb-6 flex items-center gap-4">
            <div className="relative">
              <div className="h-20 w-20 rounded-full bg-gray-100 overflow-hidden border-2 border-gray-200">
                {avatarPreview ? (
                  <img 
                    src={avatarPreview} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <UserIcon className="h-10 w-10" />
                  </div>
                )}
              </div>
              {avatarFile && (
                <div className="absolute -bottom-1 -right-1 bg-amber-500 rounded-full p-1">
                  <CameraIcon className="h-3 w-3 text-white" />
                </div>
              )}
            </div>
            <div>
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors border border-amber-200">
                <ArrowUpTrayIcon className="h-4 w-4" />
                <span className="text-sm font-medium">Upload Photo</span>
                <input 
                  type="file" 
                  accept="image/*" 
                  className="hidden" 
                  onChange={handleAvatarChange}
                />
              </label>
              <p className="text-xs text-gray-500 mt-1">JPG, PNG up to 5MB</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
              <input
                type="text"
                name="firstName"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                value={formData.firstName}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input
                type="text"
                name="lastName"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                value={formData.lastName}
                onChange={handleChange}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <PhoneIcon className="h-4 w-4 inline mr-1" />
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+254..."
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <EnvelopeIcon className="h-4 w-4 inline mr-1" />
                Email
              </label>
              <input
                type="email"
                disabled
                className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-gray-500"
                value={user?.email || ''}
              />
              <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
            </div>
          </div>
        </div>

        {/* Delivery Address */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
            <MapPinIcon className="h-5 w-5 mr-2 text-gray-500" />
            Delivery Address
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Set your default delivery address for faster checkout
          </p>
          <KenyaLocationFields
            county={formData.county}
            subCounty={formData.subCounty}
            town={formData.town}
            onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
          />
          <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Address</label>
              <textarea
                name="defaultAddress"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                value={formData.defaultAddress}
                onChange={handleChange}
                placeholder="Building, street, landmarks..."
              />
          </div>
        </div>

        {/* Order Notifications */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
            <BellIcon className="h-5 w-5 mr-2 text-gray-500" />
            Order Notifications
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Get notified about your orders from farms
          </p>
          <div className="space-y-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="notifyOnOrderConfirmed"
                checked={formData.notifyOnOrderConfirmed}
                onChange={handleChange}
                className="h-4 w-4 text-amber-600 rounded border-gray-300"
              />
              <span className="ml-3 text-sm text-gray-700">
                <span className="font-medium">Order Confirmed</span>
                <span className="block text-gray-500">Get notified when the farm confirms your order</span>
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                name="notifyOnOrderStatusChange"
                checked={formData.notifyOnOrderStatusChange}
                onChange={handleChange}
                className="h-4 w-4 text-amber-600 rounded border-gray-300"
              />
              <span className="ml-3 text-sm text-gray-700">
                <span className="font-medium">Order Updates</span>
                <span className="block text-gray-500">Get notified when your order status changes</span>
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                name="notifyOnOrderDelivered"
                checked={formData.notifyOnOrderDelivered}
                onChange={handleChange}
                className="h-4 w-4 text-amber-600 rounded border-gray-300"
              />
              <span className="ml-3 text-sm text-gray-700">
                <span className="font-medium">Order Delivered</span>
                <span className="block text-gray-500">Get notified when your order is delivered</span>
              </span>
            </label>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ClientSettings;
